/* Sticky mobile buy bar.
   Owns no cart logic: its button submits the real product form via the `form`
   attribute, so product-form.js runs the same add-to-cart path as the main
   button. This element only keeps the price, quantity note and disabled state
   in sync with the selected variant. */
class PbxStickyBuy extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;

    this.priceElement = this.querySelector('[data-pbx-sticky-price]');
    this.qtyElement = this.querySelector('[data-pbx-sticky-qty]');
    this.submitButton = this.querySelector('[data-pbx-sticky-submit]');
    this.labelElement = this.querySelector('[data-pbx-sticky-label]');

    try {
      this.variantPrices = JSON.parse(this.querySelector('[data-pbx-variant-prices]')?.textContent || '{}');
    } catch (error) {
      this.variantPrices = {};
    }

    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onVariantInputChange = this.onVariantInputChange.bind(this);
    this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);
    this.quantityInput?.addEventListener('change', this.onQuantityChange);
    this.quantityInput?.addEventListener('input', this.onQuantityChange);

    /* Read the option inputs directly as well as listening for variantChange.
       The event only helps if this element upgraded before the change; reading
       the DOM also recovers the right price on connect. */
    this.variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
    this.variantSelects?.addEventListener('change', this.onVariantInputChange);
    this.syncFromInputs();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.syncDisabledState());
      this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (!variant || event.data.sectionId !== this.sectionId) return;
        this.unitPrice = variant.price;
        this.render();
        this.syncDisabledState();
      });
    }

    this.render();
  }

  disconnectedCallback() {
    this.quantityInput?.removeEventListener('change', this.onQuantityChange);
    this.quantityInput?.removeEventListener('input', this.onQuantityChange);
    this.variantSelects?.removeEventListener('change', this.onVariantInputChange);
    this.variantUnsubscriber?.();
    this.cartUnsubscriber?.();
  }

  onQuantityChange() {
    this.render();
  }

  onVariantInputChange() {
    // Dawn re-renders asynchronously; resolve from the inputs on the next tick.
    setTimeout(() => this.syncFromInputs(), 0);
  }

  /* Builds the variant title from the checked option inputs ("Small",
     "Small / Blue") and looks its price up in the server-rendered map. */
  syncFromInputs() {
    if (!this.variantSelects) return;

    const values = [];
    this.variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select');
      if (checked) values.push(checked.value.trim());
      else if (select) values.push(select.value.trim());
    });

    if (!values.length) return;
    const price = this.variantPrices[values.join(' / ')];
    if (typeof price !== 'number') return;

    this.unitPrice = price;
    this.render();
    this.syncDisabledState();
  }

  get quantity() {
    const value = parseInt(this.quantityInput?.value, 10);
    return Number.isNaN(value) || value < 1 ? 1 : value;
  }

  /* Mirrors the main submit button rather than deriving availability again,
     so the two can never disagree. */
  syncDisabledState() {
    const mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (!mainButton || !this.submitButton) return;
    const disabled = mainButton.hasAttribute('disabled');
    this.submitButton.toggleAttribute('disabled', disabled);
    if (this.labelElement) {
      this.labelElement.textContent = mainButton.textContent.trim() || this.labelElement.textContent;
    }
  }

  render() {
    const quantity = this.quantity;
    if (this.priceElement) {
      this.priceElement.textContent = this.formatMoney(this.unitPrice * quantity);
    }
    if (this.qtyElement) {
      this.qtyElement.hidden = quantity < 2;
      this.qtyElement.textContent = quantity < 2 ? '' : `${quantity} × ${this.formatMoney(this.unitPrice)}`;
    }
  }

  /* Same token set as Shopify's money filter, so the bar matches the prices
     rendered server-side elsewhere on the page. */
  formatMoney(cents) {
    const format = this.moneyFormat;
    const value = (cents || 0) / 100;

    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const fixed = number.toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'");
        default:
          return withDelimiters(value, 2);
      }
    });
  }
}

customElements.define('pbx-sticky-buy', PbxStickyBuy);
